import { useEffect, useRef, useState } from "react";
import { BoardCard } from "../components/BoardCard";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { createBoard, getBoards, getPublicBoards } from "../lib/api";
import { navigate } from "../lib/router";
import type { Board } from "../lib/types";

export function BoardsRoute() {
  const [boards, setBoards] = useState<Board[]>([]);
  const [publicBoards, setPublicBoards] = useState<Board[]>([]);
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [creating, setCreating] = useState(false);
  const dialogInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [myData, publicData] = await Promise.all([
        getBoards(),
        getPublicBoards(),
      ]);
      if (cancelled) {
        return;
      }
      if (myData) setBoards(myData.boards);
      if (publicData) setPublicBoards(publicData.boards);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (showNewDialog) {
      dialogInputRef.current?.focus();
    }
  }, [showNewDialog]);

  async function handleCreate() {
    const label = newLabel.trim();
    if (!label || creating) return;
    setCreating(true);
    const result = await createBoard(label);
    setCreating(false);
    if (result.ok) {
      setShowNewDialog(false);
      setNewLabel("");
      navigate(`/boards/${result.data.board.id}`);
    }
  }

  return (
    <section
      id="boards-view"
      className="w-full px-4 pb-4 grid gap-[1.8rem] pt-[2.4rem] flex-1 min-h-0 overflow-auto content-start"
    >
      <div className="flex items-center justify-between gap-4 max-[720px]:items-start">
        <div>
          <h2 className="m-0 font-bold tracking-[-0.04em] text-[clamp(1.4rem,3vw,2.2rem)]">
            Your Boards
          </h2>
        </div>
        <Button
          id="boards-new-btn"
          onClick={() => setShowNewDialog(true)}
        >
          New board
        </Button>
      </div>

      <Dialog open={showNewDialog} onOpenChange={(open) => {
        setShowNewDialog(open);
        if (!open) setNewLabel("");
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New Board</DialogTitle>
          </DialogHeader>
          <Input
            ref={dialogInputRef}
            placeholder="Board name"
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleCreate();
            }}
          />
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => {
                setShowNewDialog(false);
                setNewLabel("");
              }}
            >
              Cancel
            </Button>
            <Button
              disabled={!newLabel.trim() || creating}
              onClick={handleCreate}
            >
              {creating ? "Creating..." : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div id="boards-list" className="grid grid-cols-[repeat(auto-fill,minmax(280px,1fr))] gap-4">
        {boards.map((board) => (
          <BoardCard key={board.id} board={board} />
        ))}
      </div>
      {publicBoards.length > 0 && (
        <>
          <div>
            <h2 className="m-0 font-bold tracking-[-0.04em] text-[clamp(1.4rem,3vw,2.2rem)]">
              Boards by Other Users
            </h2>
          </div>
          <div id="public-boards-list" className="grid grid-cols-[repeat(auto-fill,minmax(280px,1fr))] gap-4">
            {publicBoards.map((board) => (
              <BoardCard key={board.id} board={board} />
            ))}
          </div>
        </>
      )}
    </section>
  );
}
